import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import toast from "react-hot-toast";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import { Label } from "../../Components/ui/label";
import { FourDots } from "../../Components/FourDots";
import { AuthFooter } from "../../Components/AuthFooter";
import measureLogo from "/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png";
import { CHALLENGES } from "../../utils/cognito";
import { useDispatch } from "react-redux";
import { setToken } from "../../../redux/reducers/authSlice";
import * as Sentry from "@sentry/react";

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      const user = await Auth.signIn(email, password);
      
      if (user.challengeName === CHALLENGES.NEW_PASSWORD_REQUIRED) {
        navigate("/new-password-required", {
          state: {
            email: email,
          },
        });
        return;
      }

      dispatch(setToken({ token: user.signInUserSession.idToken.jwtToken }));
      setLoading(false);
      const attributes = user.signInUserSession?.idToken?.payload;
      const storageKey = `${attributes.email}-loginFlag`;
      const loginFlagValue = localStorage.getItem(storageKey);
      if (loginFlagValue) {
        const newLoginFlagValue = parseInt(loginFlagValue) + 1;
        localStorage.setItem(storageKey, newLoginFlagValue.toString());
      } else {
        localStorage.setItem(storageKey, "1");
      }
      // Don't navigate - let App.tsx handle route switching based on isAuthenticated
      // This ensures profile is loaded before showing dashboard
    } catch (error: any) {
      Sentry.captureException(error);
      setLoading(false);

      if (new Error(error)?.message.includes("New password required")) {
        navigate("/new-password-required", {
          state: {
            email: email,
          },
        });
        return;
      }

      if (error.code === "UserNotConfirmedException") {
        toast.error("User is not confirmed");
        await Auth.resendSignUp(email);
        navigate("/confirm-signup", {
          state: {
            email: email,
            password: password,
          },
        });
      } else {
        const errorMessage = new Error(error)?.message;
        if (errorMessage) {
          if (errorMessage.includes(":")) {
            const messageWithoutErrorCode = errorMessage.split(":")[1].trim();
            toast.error(messageWithoutErrorCode);
          } else {
            toast.error(errorMessage);
          }
        } else {
          toast.error("Something unexpected happened");
        }
      }
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <Link to="/" className="mb-8">
              <img
                src={measureLogo}
                alt="Measure Ignite"
                className="h-25 w-auto"
              />
            </Link>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                maxLength={30}
              />
              <div className="flex justify-end pt-1">
                <Link
                  to="/forgot-password"
                  className="text-sm text-orange-600 hover:text-orange-700"
                >
                  Forgot Password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={loading}
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Not a Member?{" "}
            <Link
              to="/signup"
              className="text-orange-600 hover:text-orange-700 font-semibold"
            >
              Join now
            </Link>
          </p>

          <AuthFooter />
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-orange-100 via-pink-100 to-orange-50 p-12">
        <div className="h-full flex flex-col justify-center items-center">
          <div className="max-w-lg text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              Track your impact. Share your work. Find your
              village.
            </h2>
            <p className="text-gray-700">
              Ignite helps you organize impact data, generate
              reports, and connect with your community.
            </p>
          </div>
          <FourDots size="2xl" opacity={0.7} />
        </div>
      </div>
    </div>
  );
}
